import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { ControlButton, MiniMap, Panel, useReactFlow, useStore, } from '@xyflow/react';
import { AppWindow, ArrowRightLeft, ArrowUpDown, Check, GripVertical, Minus, } from 'lucide-react';
import { installEditorAssets } from './editor-styles';
import { cn } from './lib/utils';
/** React Flow's fit-view icon, which its Controls do not export. Fills with
 *  the text color. */
function FitViewIcon({ className }) {
    return (_jsx("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 32 30", fill: "currentColor", "aria-hidden": true, className: className, children: _jsx("path", { d: "M3.692 4.63c0-.53.4-.938.939-.938h5.215V0H4.708C2.13 0 0 2.054 0 4.63v5.216h3.692V4.631zM27.354 0h-5.2v3.692h5.17c.53 0 .984.4.984.939v5.215H32V4.631A4.624 4.624 0 0027.354 0zm.954 24.83c0 .532-.4.94-.939.94h-5.215v3.768h5.215c2.577 0 4.631-2.13 4.631-4.707v-5.139h-3.692v5.139zm-23.677.94c-.531 0-.939-.4-.939-.94v-5.138H0v5.139c0 2.577 2.13 4.707 4.708 4.707h5.138V25.77H4.631z" }) }));
}
const MINI_MAP_CARD = 140;
// The card's 1px border, on both sides.
const MINI_MAP_BORDERS = 2;
// The `h-8` header and the 1px line under it.
const MINI_MAP_HEADER = 33;
// The map fills the card less its borders and header.
const MINI_MAP_WIDTH = MINI_MAP_CARD - MINI_MAP_BORDERS;
const MINI_MAP_HEIGHT = MINI_MAP_CARD - MINI_MAP_BORDERS - MINI_MAP_HEADER;
// React Flow's panel margin: the card's gap from the canvas edges.
const PANEL_MARGIN = 15;
// Corner of a node block on the map, in screen pixels: the `rounded-md` of
// the form inputs.
const MINI_MAP_NODE_RADIUS = 6;
/**
 * The map's node-block corner in graph units, so it reads as a fixed screen
 * size at any map scale (scale worked out as React Flow's MiniMap does).
 * Capped at half the thinnest block, so pills don't turn into capsules.
 */
function miniMapNodeRadius(state) {
    const [tx, ty, zoom] = state.transform;
    let minX = -tx / zoom;
    let minY = -ty / zoom;
    let maxX = minX + state.width / zoom;
    let maxY = minY + state.height / zoom;
    let thinnest = Infinity;
    for (const node of state.nodeLookup.values()) {
        const { x, y } = node.internals.positionAbsolute;
        const width = node.measured.width ?? 0;
        const height = node.measured.height ?? 0;
        if (!width || !height)
            continue;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + width);
        maxY = Math.max(maxY, y + height);
        thinnest = Math.min(thinnest, width, height);
    }
    const unitsPerPixel = Math.max((maxX - minX) / MINI_MAP_WIDTH, (maxY - minY) / MINI_MAP_HEIGHT);
    return Math.min(MINI_MAP_NODE_RADIUS * unitsPerPixel, thinnest / 2);
}
/** The minimap card. Lives inside <ReactFlow> for useReactFlow's context. */
export function MiniMapCard() {
    const { fitView } = useReactFlow();
    // The map's colors and the controls' look live in the editor stylesheet.
    useEffect(installEditorAssets, []);
    const [open, setOpen] = useState(true);
    const nodeRadius = useStore(miniMapNodeRadius);
    const canvasWidth = useStore(state => state.width);
    const canvasHeight = useStore(state => state.height);
    // How far the author dragged the card from its bottom-right corner, in
    // pixels; both stay at or below 0. Session-local.
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const drag = useRef(null);
    // Closed, the card is its header (no line under it) and borders.
    const cardHeight = open
        ? MINI_MAP_CARD
        : MINI_MAP_HEADER - 1 + MINI_MAP_BORDERS;
    // Keeps the whole card on the canvas, also after the canvas shrinks.
    const clamp = (next) => ({
        x: Math.min(0, Math.max(next.x, -(canvasWidth - MINI_MAP_CARD - 2 * PANEL_MARGIN))),
        y: Math.min(0, Math.max(next.y, -(canvasHeight - cardHeight - 2 * PANEL_MARGIN))),
    });
    const placed = clamp(offset);
    const buttonClass = 'flex h-6 w-6 items-center justify-center rounded text-foreground hover:bg-muted';
    return (_jsx(Panel, { position: "bottom-right", className: "!hidden sm:!block", style: { transform: `translate(${placed.x}px, ${placed.y}px)` }, children: _jsxs("div", { className: "overflow-hidden rounded-md border border-border bg-background shadow-sm", style: { width: MINI_MAP_CARD }, children: [_jsxs("div", { className: cn('flex h-8 items-center gap-0.5 px-1', open && 'border-b border-border'), children: [_jsx("span", { "aria-label": "Drag to move the map", title: "Drag to move the map", className: "flex h-6 w-5 cursor-grab touch-none items-center justify-center text-foreground active:cursor-grabbing", onPointerDown: event => {
                                event.stopPropagation();
                                event.currentTarget.setPointerCapture(event.pointerId);
                                drag.current = {
                                    x: event.clientX,
                                    y: event.clientY,
                                    from: placed,
                                };
                            }, onPointerMove: event => {
                                const start = drag.current;
                                if (!start)
                                    return;
                                setOffset(clamp({
                                    x: start.from.x + event.clientX - start.x,
                                    y: start.from.y + event.clientY - start.y,
                                }));
                            }, onPointerUp: event => {
                                event.currentTarget.releasePointerCapture(event.pointerId);
                                drag.current = null;
                            }, children: _jsx(GripVertical, { className: "h-3.5 w-3.5" }) }), _jsx("button", { type: "button", "aria-label": "Fit the graph in view", title: "Fit the graph in view", className: buttonClass, onClick: () => void fitView({ padding: 0.25, maxZoom: 1, duration: 300 }), children: _jsx(FitViewIcon, { className: "h-3 w-3" }) }), _jsx("button", { type: "button", "aria-label": open ? 'Hide the map' : 'Show the map', title: open ? 'Hide the map' : 'Show the map', className: cn(buttonClass, 'ml-auto'), onClick: () => setOpen(current => !current), children: open ? (_jsx(Minus, { className: "h-3.5 w-3.5" })) : (_jsx(AppWindow, { className: "h-3.5 w-3.5" })) })] }), open && (_jsx(MiniMap, { pannable: true, zoomable: true, bgColor: "transparent", nodeColor: "var(--minimap-node)", nodeStrokeWidth: 0, nodeBorderRadius: nodeRadius, maskColor: "var(--minimap-mask)", maskStrokeColor: "var(--minimap-mask-stroke)", maskStrokeWidth: 1, 
                    // The card places the map, so drop the panel's own corner
                    // position and margin.
                    className: "!static !m-0 !bg-transparent", style: { width: MINI_MAP_WIDTH, height: MINI_MAP_HEIGHT } }))] }) }));
}
/** Autofit, as the first button of the graph controls in the bottom-left
 *  corner. Its menu opens to the right of the stack. */
export function AutofitMenu({ direction, onAutofit, }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        const onPointerDown = (event) => {
            const target = event.target;
            if (target && !containerRef.current?.contains(target))
                setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                setOpen(false);
        };
        window.addEventListener('mousedown', onPointerDown);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('mousedown', onPointerDown);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);
    const choose = (direction) => {
        setOpen(false);
        onAutofit(direction);
    };
    return (_jsxs("div", { ref: containerRef, className: "super-controls-first relative", children: [_jsx(ControlButton, { title: "Autofit \u2014 re-flow nodes and reset drag positions", "aria-label": "Autofit", "aria-haspopup": "menu", "aria-expanded": open, onClick: () => setOpen(current => !current), children: direction === 'TB' ? (_jsx(ArrowUpDown, { className: "!fill-none", strokeWidth: 2.75 })) : (_jsx(ArrowRightLeft, { className: "!fill-none", strokeWidth: 2.75 })) }), open && (_jsxs("div", { role: "menu", className: "absolute top-0 left-full z-20 ml-2 w-56 overflow-hidden rounded-md border border-border bg-popover shadow-md", children: [_jsxs("button", { type: "button", role: "menuitem", className: "flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-muted/60 border-b", onClick: () => choose('LR'), children: [_jsx(ArrowRightLeft, { className: "h-3.5 w-3.5 text-primary flex-shrink-0" }), _jsxs("span", { children: [_jsx("span", { className: "block font-medium", children: "Autofit horizontal" }), _jsx("span", { className: "block text-[11px] text-muted-foreground", children: "Lay out left to right." })] }), direction === 'LR' && (_jsx(Check, { className: "ml-auto h-3.5 w-3.5 flex-shrink-0" }))] }), _jsxs("button", { type: "button", role: "menuitem", className: "flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-muted/60", onClick: () => choose('TB'), children: [_jsx(ArrowUpDown, { className: "h-3.5 w-3.5 text-primary flex-shrink-0" }), _jsxs("span", { children: [_jsx("span", { className: "block font-medium", children: "Autofit vertical" }), _jsx("span", { className: "block text-[11px] text-muted-foreground", children: "Lay out top to bottom." })] }), direction === 'TB' && (_jsx(Check, { className: "ml-auto h-3.5 w-3.5 flex-shrink-0" }))] })] }))] }));
}
//# sourceMappingURL=CanvasControls.js.map